import { Trans } from 'react-i18next';
import styled from 'styled-components';

import { HealthcareProviderEwusConfiguration } from '@dreryk/egabinet-ewus-frontend-api';
import { HealthcareProviderEzwmConfiguration } from '@dreryk/egabinet-ezwm-frontend-api';
import {
   HealthcareProviderNfzConfiguration,
   NfzContractingPartyType,
   NfzDepartment,
} from '@dreryk/egabinet-nfz-configuration-frontend-api';

import { BindingCreator, useTranslate } from '@dreryk/react-common';
import { FormRouteLeavingPrompt, FormValidationSnackbar, StyledFlex, StyledMaxWidth } from '@dreryk/react-components';

import { StyledFormSectionHeader } from '../../../common/StyledFormSectionHeader';
import { nfzSettingsTranslationKeys } from '../../../translations';

import { ContractingPartyTypeRadioGroup } from './ContractingPartyTypeRadioGroup';
import { EwusAutomaticStatusCheckToggle } from './EwusAutomaticStatusCheckToggle';
import { EwusIntegrationToggle } from './EwusIntegrationToggle';
import { EzwmSenderCodeForm } from './EzwmSenderCode/EzwmSenderCodeForm';
import { IntegrationSettingInfoNotification } from './IntegrationSettingInfoNotification';
import { NfzDepartmentSelect } from './NfzDepartmentSelect';
import { NfzLoginLink } from './NfzLoginLink';
import { useNfzSettingsFormController } from './NfzSettingsForm.hooks';
import { NfzConfigurationFormBean, NfzDepartmentWithIdentifierRequired } from './NfzSettingsForm.types';
import { NfzSettingsSaveAction } from './NfzSettingsSaveAction';
import { ProviderCodeInput } from './ProviderCodeInput';

const providerCodeVisible = (
   register: BindingCreator<NfzConfigurationFormBean>,
   contractingPartyType: NfzContractingPartyType | undefined
): register is BindingCreator<
   {
      nfzDepartment: NfzDepartmentWithIdentifierRequired<true>;
      contractingPartyType: 'PERSON' | 'PROVIDER';
      providerCode: string | null;
   } & { contractingPartyType: 'PROVIDER' }
> => {
   return contractingPartyType === 'PROVIDER';
};

type NfzSettingsFormProps = {
   readonly nfzConfiguration: HealthcareProviderNfzConfiguration | null;
   readonly ewusConfiguration: HealthcareProviderEwusConfiguration | null;
   readonly ezwmConfiguration: HealthcareProviderEzwmConfiguration | null;
};

const StyledNfzIntegrationSection = styled(StyledFlex)`
   width: 230px;
`;
StyledNfzIntegrationSection.displayName = 'StyledNfzIntegrationSection';

const isContractingPartyTypeVisible = (
   nfzDepartment: NfzDepartment | null,
   register: BindingCreator<NfzConfigurationFormBean>
): register is BindingCreator<{
   contractingPartyType: 'PERSON' | 'PROVIDER';
   nfzDepartment: NfzDepartmentWithIdentifierRequired<true>;
   providerCode: string | null;
}> => {
   return Boolean(nfzDepartment);
};

export const NfzSettingsForm = (props: NfzSettingsFormProps): JSX.Element => {
   const translate = useTranslate();
   const { register, handleSubmit, formState, watch } = useNfzSettingsFormController({
      nfzConfiguration: props.nfzConfiguration,
   });

   const { nfzDepartment, contractingPartyType } = watch(['nfzDepartment', 'contractingPartyType']);

   const ewusIntegration = Boolean(props.ewusConfiguration);
   return (
      <>
         <StyledFlex gap={'142px'}>
            <StyledNfzIntegrationSection direction={'column'} gap={'12px'}>
               <StyledFormSectionHeader>{translate(nfzSettingsTranslationKeys.nfzIntegration)}</StyledFormSectionHeader>
               <NfzDepartmentSelect name={'nfzDepartment'} register={register} required />
               {isContractingPartyTypeVisible(nfzDepartment, register) ? (
                  <ContractingPartyTypeRadioGroup
                     name={'contractingPartyType'}
                     register={register}
                     defaultValue={
                        props.nfzConfiguration?.contractingPartyType ?? nfzDepartment?.identifierRequired
                           ? undefined
                           : 'PERSON'
                     }
                     required
                  />
               ) : null}
               {providerCodeVisible(register, contractingPartyType) && (
                  <StyledMaxWidth maxWidth={260}>
                     <ProviderCodeInput name={'providerCode'} register={register} required defaultValue={null} />
                  </StyledMaxWidth>
               )}
               <NfzSettingsSaveAction handleSubmit={handleSubmit} />
            </StyledNfzIntegrationSection>
            <StyledFlex direction={'column'} gap={'12px'}>
               <StyledFormSectionHeader>
                  {translate(nfzSettingsTranslationKeys.integrationSettings)}
               </StyledFormSectionHeader>

               <StyledFlex direction={'column'} gap={'16px'}>
                  {ewusIntegration || props.ezwmConfiguration ? (
                     <span>
                        <Trans
                           i18nKey={nfzSettingsTranslationKeys.afterSaveEwusOrEzwmLoginGoToNfzLogin}
                           components={{
                              link: <NfzLoginLink testId={'goToNfzLogin'} />,
                           }}
                        />
                     </span>
                  ) : null}
                  <EzwmSenderCodeForm senderCode={props.ezwmConfiguration?.senderCode ?? null} />
                  <IntegrationSettingInfoNotification />
                  <EwusIntegrationToggle initialValue={ewusIntegration} disabled={props.nfzConfiguration === null} />
                  {ewusIntegration ? (
                     <EwusAutomaticStatusCheckToggle
                        initialValue={props.ewusConfiguration?.automaticStatusCheck ?? false}
                        disabled={props.nfzConfiguration === null}
                     />
                  ) : null}
               </StyledFlex>
            </StyledFlex>
         </StyledFlex>

         <FormRouteLeavingPrompt formState={formState} />
         <FormValidationSnackbar formState={formState} />
      </>
   );
};
